import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from 'recharts';
import FreshnessIndicator from '../../../components/FreshnessIndicator';

// Bookmaker color palette for distinguishing lines
const BOOKMAKER_COLORS: Record<string, { home: string; away: string }> = {
  pinnacle: { home: '#a855f7', away: '#c084fc' },      // Purple (sharp)
  betfair: { home: '#8b5cf6', away: '#a78bfa' },       // Violet (sharp)
  circa: { home: '#7c3aed', away: '#a78bfa' },         // Violet dark (sharp)
  westgate: { home: '#6366f1', away: '#818cf8' },      // Indigo (sharp)
  bookmaker: { home: '#4f46e5', away: '#6366f1' },     // Indigo dark (sharp)
  draftkings: { home: '#f97316', away: '#fb923c' },    // Orange (square)
  fanduel: { home: '#ef4444', away: '#f87171' },       // Red (square)
  betmgm: { home: '#eab308', away: '#facc15' },        // Yellow (square)
  caesars: { home: '#22c55e', away: '#4ade80' },       // Green (square)
  pointsbet: { home: '#06b6d4', away: '#22d3ee' },     // Cyan (square)
  betrivers: { home: '#ec4899', away: '#f472b6' },     // Pink (square)
  unibet: { home: '#14b8a6', away: '#2dd4bf' },        // Teal (square)
  default: { home: '#6b7280', away: '#9ca3af' },       // Gray fallback
};

// Get color for bookmaker
function getBookmakerColor(bookmaker: string, isHome: boolean): string {
  const key = bookmaker.toLowerCase();
  for (const [name, colors] of Object.entries(BOOKMAKER_COLORS)) {
    if (key.includes(name)) {
      return isHome ? colors.home : colors.away;
    }
  }
  return isHome ? BOOKMAKER_COLORS.default.home : BOOKMAKER_COLORS.default.away;
}

function formatTimeAgo(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

interface LiveOdds {
  bookmaker: string;
  homePrice: number | null;
  awayPrice: number | null;
  isSharp: boolean;
  timestamp: string | null;
}

interface LineChartDataPoint {
  time: string;
  timeNum: number;
  [key: string]: string | number | null;
}

interface Event {
  homeTeam: string;
  awayTeam: string;
  isLive?: boolean;
}

interface SportsbookOddsChartProps {
  event: Event;
  lineChartData: LineChartDataPoint[];
  liveOdds: LiveOdds[];
  bookmakersInData: string[];
  selectedBookmakers: Set<string>;
  toggleBookmaker: (bookmaker: string) => void;
  sharpBooks: string[];
  squareBooks: string[];
  lastOddsUpdate: string | null;
}

export default function SportsbookOddsChart({
  event,
  lineChartData,
  liveOdds,
  bookmakersInData,
  selectedBookmakers,
  toggleBookmaker,
  sharpBooks,
  squareBooks,
  lastOddsUpdate,
}: SportsbookOddsChartProps) {
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">SPORTSBOOK ODDS TRACKER</h2>
          <p className="text-sm text-gray-400 mt-1">
            Line movement across bookmakers - Sharp books in purple, Square books in warm colors
          </p>
        </div>
        <div className="flex items-center gap-2">
          <FreshnessIndicator
            lastUpdate={lastOddsUpdate}
            label="Last Update"
            showLabel={true}
            warningThreshold={360}
            staleThreshold={600}
            compact={true}
          />
        </div>
      </div>

      {/* Bookmaker Filter Buttons */}
      <div className="flex flex-wrap gap-2 items-center mb-4 pb-4 border-b border-gray-800">
        <button
          onClick={() => toggleBookmaker('all')}
          className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
            selectedBookmakers.has('all')
              ? 'bg-blue-500 text-white border border-blue-600'
              : 'bg-surface-100 text-gray-400 border border-gray-600 hover:bg-surface-200'
          }`}
        >
          All Books
        </button>
        <div className="w-px h-6 bg-gray-700 mx-1"></div>
        {sharpBooks.length > 0 && (
          <>
            <span className="text-xs text-purple-400 font-medium px-1">Sharp:</span>
            {sharpBooks.map(book => (
              <button
                key={book}
                onClick={() => toggleBookmaker(book)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  selectedBookmakers.has(book)
                    ? 'bg-purple-500 text-white border border-purple-600'
                    : 'bg-surface-100 text-gray-400 border border-gray-600 hover:bg-surface-200 hover:text-purple-400'
                }`}
              >
                {book}
              </button>
            ))}
          </>
        )}
        {squareBooks.length > 0 && (
          <>
            <div className="w-px h-6 bg-gray-700 mx-1"></div>
            <span className="text-xs text-orange-400 font-medium px-1">Square:</span>
            {squareBooks.map(book => (
              <button
                key={book}
                onClick={() => toggleBookmaker(book)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  selectedBookmakers.has(book)
                    ? 'bg-orange-500 text-white border border-orange-600'
                    : 'bg-surface-100 text-gray-400 border border-gray-600 hover:bg-surface-200 hover:text-orange-400'
                }`}
              >
                {book}
              </button>
            ))}
          </>
        )}
      </div>

      {/* Chart + Live Odds Index Layout */}
      <div className="flex gap-6">
        {/* Main Chart Area - 75% */}
        <div className="flex-1 min-w-0">
          <div className="h-96">
            {lineChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lineChartData} margin={{ top: 10, right: 30, bottom: 10, left: 10 }}>
                  <XAxis
                    dataKey="time"
                    stroke="#6b7280"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => v}
                  />
                  <YAxis
                    stroke="#6b7280"
                    tick={{ fontSize: 11 }}
                    domain={['auto', 'auto']}
                    tickFormatter={(v) => v.toFixed(2)}
                    label={{ value: 'Decimal Odds', angle: -90, position: 'insideLeft', fill: '#6b7280', fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1a2e',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number, name: string) => [value?.toFixed(3) || 'N/A', name]}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: '10px' }}
                    formatter={(value) => <span className="text-xs">{value}</span>}
                  />
                  <ReferenceLine y={2.0} stroke="#374151" strokeDasharray="3 3" label={{ value: 'Even', fill: '#6b7280', fontSize: 10 }} />

                  {/* Generate lines for each bookmaker */}
                  {bookmakersInData.map((bookmaker) => (
                    <>
                      <Line
                        key={`${bookmaker}-home`}
                        type="monotone"
                        dataKey={`${bookmaker}_home`}
                        name={`${bookmaker} (${event.homeTeam})`}
                        stroke={getBookmakerColor(bookmaker, true)}
                        strokeWidth={2}
                        dot={{ r: 3, fill: getBookmakerColor(bookmaker, true) }}
                        connectNulls
                        activeDot={{ r: 5, stroke: '#fff', strokeWidth: 1 }}
                      />
                      <Line
                        key={`${bookmaker}-away`}
                        type="monotone"
                        dataKey={`${bookmaker}_away`}
                        name={`${bookmaker} (${event.awayTeam})`}
                        stroke={getBookmakerColor(bookmaker, false)}
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={{ r: 3, fill: getBookmakerColor(bookmaker, false) }}
                        connectNulls
                        activeDot={{ r: 5, stroke: '#fff', strokeWidth: 1 }}
                      />
                    </>
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <div className="text-4xl mb-2">📊</div>
                  <div>No odds history available</div>
                  <div className="text-xs text-gray-500 mt-1">Odds data will appear once collected</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Live Odds Index - 25% */}
        <div className="w-72 flex-shrink-0 border-l border-gray-800 pl-6">
          <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wide mb-3">
            Live Odds Index
          </h3>
          {liveOdds.length > 0 ? (
            <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
              {liveOdds.map((odds) => (
                <div
                  key={odds.bookmaker}
                  className={`p-3 rounded-lg border ${
                    odds.isSharp
                      ? 'bg-purple-500/10 border-purple-500/30'
                      : 'bg-surface-50 border-gray-700'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${odds.isSharp ? 'text-purple-400' : 'text-gray-300'}`}>
                      {odds.bookmaker}
                    </span>
                    {odds.isSharp && (
                      <span className="text-[10px] bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">SHARP</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-surface-100 rounded px-2 py-1.5">
                      <div className="text-gray-500 text-[10px] uppercase">Home</div>
                      <div className="text-green-400 font-mono font-medium">
                        {odds.homePrice?.toFixed(2) || '---'}
                      </div>
                    </div>
                    <div className="bg-surface-100 rounded px-2 py-1.5">
                      <div className="text-gray-500 text-[10px] uppercase">Away</div>
                      <div className="text-red-400 font-mono font-medium">
                        {odds.awayPrice?.toFixed(2) || '---'}
                      </div>
                    </div>
                  </div>
                  {odds.timestamp && (
                    <div className="text-[10px] text-gray-500 mt-1.5 text-right">
                      {formatTimeAgo(odds.timestamp)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <div className="text-2xl mb-2">📈</div>
              <div className="text-xs">No live odds available</div>
            </div>
          )}

          {/* Legend */}
          <div className="mt-4 pt-4 border-t border-gray-800">
            <div className="text-xs text-gray-500 mb-2">Legend</div>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-purple-500"></div>
                <span className="text-gray-400">Sharp Books</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-orange-500"></div>
                <span className="text-gray-400">Square Books</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-gray-500" style={{ borderTop: '2px dashed' }}></div>
                <span className="text-gray-400">Away Team (dashed)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
